import React, { useState } from 'react';
import { Table, Tag, Button, Space, Modal, message } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import {
  listContractorUsers,
  resendInvitation,
  dissociateUser,
} from '../../services/contractor-invitation.service';
import { InviteUserModal } from './InviteUserModal';
import type { ContractorUserEntry } from '../../types/contractor-invitation.types';

interface Props {
  contractorId: string;
}

const statusColor: Record<string, string> = {
  ACTIVE: 'green',
  PENDING: 'blue',
  EXPIRED: 'orange',
};

const statusLabel: Record<string, string> = {
  ACTIVE: 'Ativo',
  PENDING: 'Convite Pendente',
  EXPIRED: 'Convite Expirado',
};

export const ContractorUsersTable: React.FC<Props> = ({ contractorId }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const queryClient = useQueryClient();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [dissociatingId, setDissociatingId] = useState<string | null>(null);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['contractor-users', contractorId],
    queryFn: () => listContractorUsers(contractorId),
    enabled: Boolean(contractorId),
  });

  const handleResend = async (entry: ContractorUserEntry) => {
    setResendingId(entry.id);
    try {
      await resendInvitation(contractorId, entry.id);
      message.success(`Convite reenviado para ${entry.email}`);
      queryClient.invalidateQueries({ queryKey: ['contractor-users', contractorId] });
    } catch {
      message.error('Erro ao reenviar convite');
    } finally {
      setResendingId(null);
    }
  };

  const handleDissociate = (entry: ContractorUserEntry) => {
    Modal.confirm({
      title: 'Desassociar usuário',
      content: `Tem certeza que deseja remover o vínculo de ${entry.name} com este contratante?`,
      okText: 'Desassociar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        setDissociatingId(entry.id);
        try {
          await dissociateUser(contractorId, entry.id);
          message.success(`${entry.name} foi desassociado do contratante`);
          queryClient.invalidateQueries({ queryKey: ['contractor-users', contractorId] });
        } catch {
          message.error('Erro ao desassociar usuário');
        } finally {
          setDissociatingId(null);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColor[status] || 'default'}>{statusLabel[status] || status}</Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: unknown, entry: ContractorUserEntry) => (
        <Space>
          {entry.type === 'INVITATION' && (
            <Button
              size="small"
              onClick={() => handleResend(entry)}
              loading={resendingId === entry.id}
            >
              Reenviar Convite
            </Button>
          )}
          {entry.type === 'USER' && isAdmin && (
            <Button
              size="small"
              danger
              onClick={() => handleDissociate(entry)}
              loading={dissociatingId === entry.id}
            >
              Desassociar
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => setInviteModalOpen(true)}
        >
          Incluir Usuário
        </Button>
      </div>
      <Table
        rowKey="id"
        dataSource={entries}
        columns={columns}
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: 'Nenhum usuário ou convite associado a este contratante.' }}
      />
      <InviteUserModal
        open={inviteModalOpen}
        contractorId={contractorId}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={() => {
          setInviteModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['contractor-users', contractorId] });
        }}
      />
    </>
  );
};
